/**
 * TeamV3 — « Qui se bat pour toi » (route `/attorneys`).
 *
 * Transposition iso-pixel de l'export Claude Design du 23/09/2026,
 * `design-refs/team-v3-ref/` (bundle.html = la référence, template.html =
 * le DOM, page-source.jsx = la logique). Le corps de `<main>` ci-dessous
 * EST le DOM de template.html : mêmes balises, mêmes classes, mêmes styles
 * inline, même ordre. Il a été produit par conversion mécanique, pas
 * retapé ; les textes vivent dans `i18n/teamV3.json`, le français y est
 * copié de la référence au caractère près.
 *
 * UNE exception, décidée par Romain : « Tout ça pour 38 €/mois. » devient
 * « Tout ça pour 34 €/mois. » (`hero.t11`). Rien d'autre ne bouge.
 *
 * Écarts de forme imposés par React, sans effet sur le rendu :
 *   - `<image-slot>` → `TV3Slot` : la géométrie que le composant image-slot
 *     du bundle calcule en JS (cadre absolu `overflow:hidden`, rond pour
 *     `shape="circle"` ; image absolue centrée par translate(-50 %, -50 %),
 *     largeur et hauteur en % du cadre pour le couvrir).
 *   - `{{ placeholders }}` → état React porté de page-source.jsx : `faq`
 *     (-1 par défaut, un clic ouvre ou referme, une seule ouverte) et
 *     `scrollJur(dir)` sur `.tp-jur-rail`.
 *   - Les espaces entre balises : seuls restent ceux qu'un rendu HTML
 *     peindrait (`{" "}`), les autres sont repliés par le navigateur.
 *
 * Liens : ceux qui visent « # » dans la référence (LinkedIn des juristes,
 * « Voir tous les épisodes ») restent inertes, leurs pages arriveront plus
 * tard. « Analyser mon dossier » garde `href="#"` et mène au dépôt
 * (`useStartRoute`), comme les CTA de la home.
 *
 * Chrome : header (`PublicHeader`, mode `authCta="dashboard"`), footer
 * (`SiteFooterV2`) et bouton WhatsApp sont les composants communs du site,
 * décision Romain du 23/09/2026. Ils diffèrent de la référence là où elle
 * a figé le chrome de 1440 px : burger du tiroir mobile et WhatsApp à
 * 52 px sous 640 px, et le 5e lien « Tous les guides » du footer.
 *
 * L'ancienne page (`TeamPage`) reste dans le dépôt, non routée.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import PageHead from '../components/seo/PageHead';
import useStartRoute from '../hooks/useStartRoute';
import { useSiteLanguage, readSiteCountry } from '../hooks/useSiteLanguage';
import { createT } from '../i18n/createT';
import { getPageMetadata } from '../lib/seo/metadata';
import dict from '../i18n/teamV3.json';
import '../styles/team-v3.css';

// uuid de l'export → fichier servi (mêmes octets, cf. public/team-v3/).
const IMG = {
  '9706b611-1d55-4a62-a82d-711e1a88fa31': '/team-v3/hero-visage-camille.webp',
  '75133d1a-ace6-423b-abff-c3dfefab7b87': '/team-v3/hero-visage-juriste-1.webp',
  '5c447413-01a9-4a0b-92cf-1234c5ab0b1e': '/team-v3/hero-visage-juriste-2.webp',
  'e69a10f8-58a6-4d13-b0f3-297c388f13b4': '/team-v3/hero-visage-avocat.webp',
  '44130110-0130-4a8f-b9ca-d233c924f2bb': '/team-v3/hero-lea-vermeulen.webp',
  '3faa4ca3-d926-41e4-bbf2-51f1176110b7': '/team-v3/juriste-lea-vermeulen.png',
  '77a2d108-04e1-4afc-a9b5-0a88395439a6': '/team-v3/juriste-nadia-belhadj.webp',
  '8a8e253b-15c4-4c61-9303-ff18046cf220': '/team-v3/juriste-thomas-lenaerts.webp',
  'd1cd59af-ceb4-4deb-90c3-190e0463f73a': '/team-v3/juriste-julie-moreau.webp',
  '3e2a996f-7709-4f9f-b230-b34069a2907b': '/team-v3/juriste-karim-haddad.webp',
  '2dbc1d78-228d-4595-bcef-bdf0b556c5ce': '/team-v3/episode-huissier.webp',
  '13e0d6c0-dfe3-45e9-9671-7d9bd7d5d0f9': '/team-v3/episode-licenciement.webp',
  '07f3e16d-e6d0-4921-92b6-825a660f5efc': '/team-v3/logo-archer.svg',
};

/**
 * L'image-slot du bundle, sans l'éditeur : cadre absolu qui rogne, image
 * absolue centrée, dimensionnée pour couvrir le cadre (même calcul que le
 * composant : échelle = max(cadre/image) sur les deux axes, exprimée en %
 * du cadre). `object-fit` seul ne donne pas le même arrondi de pixels.
 * `aspect-ratio: 3/2` est le `:host` du composant : c'est lui qui donne
 * leur hauteur aux vignettes d'épisode (la hauteur à 100 % ne se résout
 * pas contre un parent dimensionné par son seul aspect-ratio).
 */
function TV3Slot({ src, shape, hostStyle, ...rest }) {
  const hostRef = useRef(null);
  const imgRef = useRef(null);
  useEffect(() => {
    const host = hostRef.current;
    const img = imgRef.current;
    if (!host || !img) return undefined;
    const apply = () => {
      const iw = img.naturalWidth; const ih = img.naturalHeight;
      const fw = host.clientWidth; const fh = host.clientHeight;
      if (!iw || !ih || !fw || !fh) return;
      const base = Math.max(fw / iw, fh / ih);
      img.style.width = `${(iw * base / fw) * 100}%`;
      img.style.height = `${(ih * base / fh) * 100}%`;
    };
    img.addEventListener('load', apply);
    if (img.complete) apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(host);
    return () => { img.removeEventListener('load', apply); if (ro) ro.disconnect(); };
  }, [src]);
  return (
    <span ref={hostRef} className="tv3-slot" {...rest} style={{ position: 'relative', aspectRatio: '3 / 2', ...hostStyle }}>
      <span
        className="tv3-slot-frame"
        style={{ display: 'block', position: 'absolute', inset: 0, overflow: 'hidden', background: 'rgba(127,127,127,.08)', borderRadius: shape === 'circle' ? '50%' : undefined }}
      >
        <img
          ref={imgRef}
          src={src}
          alt=""
          draggable={false}
          style={{ position: 'absolute', display: 'block', maxWidth: 'none', transform: 'translate(-50%,-50%)', width: '100%', height: '100%', left: '50%', top: '50%' }}
        />
      </span>
    </span>
  );
}

// Liens « # » de la référence : inertes, aucune navigation.
const inert = (ev) => ev.preventDefault();


export default function TeamV3() {
  const navigate = useNavigate();
  const [language] = useSiteLanguage();
  const country = readSiteCountry();
  const t = React.useMemo(() => createT(dict, language), [language]);
  const metadata = getPageMetadata ? getPageMetadata('attorneys', language) : null;
  const startRoute = useStartRoute();
  const goStart = useCallback((ev) => { ev.preventDefault(); navigate(startRoute); }, [navigate, startRoute]);

  // page-source.jsx : une seule question ouverte à la fois, -1 = aucune.
  const [faq, setFaq] = useState(-1);
  const toggleFaq = useCallback((i) => setFaq((f) => (f === i ? -1 : i)), []);

  // page-source.jsx › scrollJur : trois quarts de la largeur visible du rail.
  const scrollJur = useCallback((dir, ev) => {
    const scope = ev && ev.currentTarget && ev.currentTarget.closest('section');
    const el = (scope || document).querySelector('.tp-jur-rail');
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75) });
  }, []);

  return (
    <div data-testid="team-page" className="team-page" lang={language} data-country={country} data-team-v3="">
      {metadata && <PageHead metadata={metadata} />}
      <PublicHeader authCta="dashboard" />
      <main style={{ maxWidth: "100vw", overflowX: "hidden", background: "rgb(244, 244, 241)" }}>
        <section className="archer-hero-section" style={{ position: "relative", paddingLeft: "40px", paddingRight: "40px", overflow: "hidden", background: "rgb(255, 255, 255)" }}>
          <div aria-hidden="true" className="archer-hero-halo"></div>
          <div style={{ position: "relative", maxWidth: "940px", margin: "0px auto" }}>
            <div className="tp-hero-grid" style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: "48px", alignItems: "center" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", paddingLeft: "14px" }}>
                    <span title={t("hero.t01")} style={{ width: "46px", height: "46px", borderRadius: "50%", overflow: "hidden", border: "2.5px solid rgb(255, 255, 255)", boxShadow: "0 2px 8px rgba(10,10,15,0.14)", marginLeft: "-14px", background: "rgb(244, 244, 241)", display: "block" }}>
                      <TV3Slot src={IMG["9706b611-1d55-4a62-a82d-711e1a88fa31"]} data-to-fill="" id="tp-hero-f1" shape="circle" hostStyle={{ display: "block", width: "41px", height: "41px" }} />
                    </span>
                    <span title={t("hero.t02")} style={{ width: "46px", height: "46px", borderRadius: "50%", overflow: "hidden", border: "2.5px solid rgb(255, 255, 255)", boxShadow: "0 2px 8px rgba(10,10,15,0.14)", marginLeft: "-14px", background: "rgb(244, 244, 241)", display: "block" }}>
                      <TV3Slot src={IMG["75133d1a-ace6-423b-abff-c3dfefab7b87"]} data-to-fill="" id="tp-hero-f2" shape="circle" hostStyle={{ display: "block", width: "41px", height: "41px" }} />
                    </span>
                    <span title={t("hero.t03")} style={{ width: "46px", height: "46px", borderRadius: "50%", overflow: "hidden", border: "2.5px solid rgb(255, 255, 255)", boxShadow: "0 2px 8px rgba(10,10,15,0.14)", marginLeft: "-14px", background: "rgb(244, 244, 241)", display: "block" }}>
                      <TV3Slot src={IMG["5c447413-01a9-4a0b-92cf-1234c5ab0b1e"]} data-to-fill="" id="tp-hero-f3" shape="circle" hostStyle={{ display: "block", width: "41px", height: "41px" }} />
                    </span>
                    <span title={t("hero.t04")} style={{ width: "46px", height: "46px", borderRadius: "50%", overflow: "hidden", border: "2.5px solid rgb(255, 255, 255)", boxShadow: "0 2px 8px rgba(10,10,15,0.14)", marginLeft: "-14px", background: "rgb(244, 244, 241)", display: "block" }}>
                      <TV3Slot src={IMG["e69a10f8-58a6-4d13-b0f3-297c388f13b4"]} data-to-fill="" id="tp-hero-f4" shape="circle" hostStyle={{ display: "block", width: "41px", height: "41px" }} />
                    </span>
                  </div>
                  <span style={{ fontSize: "13.5px", fontWeight: "600", letterSpacing: "-0.008em", color: "rgb(42, 42, 53)" }}>
                    {t("hero.t05")}
                  </span>
                </div>
                <h1 className="team-h1 archer-hero-title" style={{ textAlign: "left" }}>
                  {t("hero.t06")}
                  <br className="team-h1-br" />
                  {" "}
                  <span className="archer-hero-accent">
                    {t("hero.t07")}
                  </span>
                </h1>
                <div style={{ marginTop: "28px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "12px", fontSize: "16px", fontWeight: "500", lineHeight: "1.5", color: "rgb(42, 42, 53)" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1A56DB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "0 0 auto", transform: "translateY(2px)" }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {t("hero.t08")}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "12px", fontSize: "16px", fontWeight: "500", lineHeight: "1.5", color: "rgb(42, 42, 53)" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1A56DB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "0 0 auto", transform: "translateY(2px)" }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {t("hero.t09")}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "12px", fontSize: "16px", fontWeight: "500", lineHeight: "1.5", color: "rgb(42, 42, 53)" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1A56DB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "0 0 auto", transform: "translateY(2px)" }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {t("hero.t10")}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "12px", fontSize: "16px", fontWeight: "500", lineHeight: "1.5", color: "rgb(42, 42, 53)" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#1A56DB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: "0 0 auto", transform: "translateY(2px)" }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {t("hero.t11")}
                  </div>
                </div>
                <a href="#equipe" style={{ display: "inline-flex", alignItems: "center", gap: "9px", marginTop: "28px", fontSize: "14px", fontWeight: "700", letterSpacing: "-0.01em", color: "rgb(26, 86, 219)", textDecoration: "none" }}>
                  {t("hero.t12")}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <polyline points="19 12 12 19 5 12" />
                  </svg>
                </a>
              </div>
              <div className="tp-hero-photo" style={{ position: "relative" }}>
                <div style={{ borderRadius: "22px", overflow: "hidden", background: "rgb(233, 233, 228)", aspectRatio: "4 / 5", boxShadow: "0 18px 44px rgba(10,10,15,0.12)" }}>
                  <TV3Slot src={IMG["44130110-0130-4a8f-b9ca-d233c924f2bb"]} id="tp-hero-grande-3" shape="rect" hostStyle={{ display: "block", width: "100%", height: "100%" }} />
                </div>
                <div style={{ position: "absolute", left: "-20px", bottom: "24px", maxWidth: "268px", background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", borderRadius: "16px", padding: "15px 16px", boxShadow: "0 12px 32px rgba(10,10,15,0.12)" }}>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "800", letterSpacing: "-0.022em", color: "rgb(10, 10, 15)" }}>
                      {t("hero.t13")}
                    </div>
                    <div style={{ marginTop: "2px", fontSize: "11.5px", fontWeight: "600", letterSpacing: "0.01em", color: "rgb(26, 86, 219)" }}>
                      {t("hero.t14")}
                    </div>
                  </div>
                  <div style={{ marginTop: "12px", fontSize: "13.5px", fontWeight: "500", lineHeight: "1.5", color: "rgb(10, 10, 15)" }}>
                    {t("hero.t15")}
                  </div>
                  <div style={{ marginTop: "12px", paddingTop: "11px", borderTop: "1px solid rgb(244, 244, 241)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11.5px", fontWeight: "500", color: "rgb(110, 110, 104)" }}>
                      {t("hero.t16")}
                      <img alt="Archer" src={IMG["07f3e16d-e6d0-4921-92b6-825a660f5efc"]} style={{ height: "17px", width: "auto", display: "block" }} />
                    </span>
                    <a href="#" onClick={inert} aria-label={t("hero.t17")} style={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: "11.5px", fontWeight: "700", color: "rgb(26, 86, 219)", textDecoration: "none" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.76-2.05C21.1 8.65 22 10.9 22 14.03V21h-4v-6.2c0-1.48-.53-2.49-1.85-2.49-1.01 0-1.61.68-1.87 1.34-.1.24-.13.57-.13.9V21h-4V9Z" />
                      </svg>
                      {t("hero.t18")}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="equipe" style={{ padding: "76px 40px 84px", background: "rgb(255, 255, 255)", borderTop: "1px solid rgb(236, 236, 234)" }}>
          <div style={{ maxWidth: "1020px", margin: "0px auto" }}>
            <div style={{ maxWidth: "640px", marginBottom: "30px" }}>
              <h2 style={{ fontSize: "clamp(28px, 3.4vw, 40px)", fontFamily: "'Inter Tight', -apple-system, BlinkMacSystemFont, system-ui, sans-serif", fontWeight: "600", letterSpacing: "-0.032em", lineHeight: "1.14", margin: "0px 0px 12px", color: "rgb(10, 10, 15)" }}>
                {t("equipe.t01")}
              </h2>
              <p style={{ margin: "0px", fontSize: "15.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)" }}>
                {t("equipe.t02")}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "14px" }}>
              <div style={{ fontSize: "12px", fontWeight: "800", letterSpacing: "0.1em", textTransform: "uppercase", color: "rgb(26, 86, 219)" }}>
                {t("equipe.t03")}
              </div>
              <div style={{ flex: "1 1 0%" }}></div>
              <button type="button" onClick={(ev) => scrollJur(-1, ev)} aria-label={t("equipe.t04")} className="tp-jur-nav" style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", color: "rgb(107, 114, 128)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", font: "inherit", transition: "all .15s ease" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button type="button" onClick={(ev) => scrollJur(1, ev)} aria-label={t("equipe.t05")} className="tp-jur-nav" style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", color: "rgb(107, 114, 128)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", font: "inherit", transition: "all .15s ease" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
            <div className="tp-jur-rail" style={{ display: "flex", gap: "14px", overflowX: "auto", scrollSnapType: "x mandatory", padding: "2px 0 8px", marginBottom: "20px" }}>
              <div style={{ flex: "0 0 236px", scrollSnapAlign: "start", background: "rgb(250, 250, 248)", border: "1.5px dashed rgb(212, 212, 206)", borderRadius: "18px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ aspectRatio: "4 / 5", background: "rgb(233, 233, 228)" }}>
                  <TV3Slot src={IMG["3faa4ca3-d926-41e4-bbf2-51f1176110b7"]} id="tp-jur-lea" shape="rect" hostStyle={{ display: "block", width: "100%", height: "100%" }} />
                </div>
                <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: "7px", flex: "1 1 0%" }}>
                  <div style={{ fontSize: "17px", fontWeight: "800", letterSpacing: "-0.025em", color: "rgb(10, 10, 15)" }}>
                    {t("equipe.t06")}
                  </div>
                  <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "rgb(26, 86, 219)" }}>
                    {t("equipe.t07")}
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "400", lineHeight: "1.55", color: "rgb(90, 90, 101)" }}>
                    {t("equipe.t08")}
                  </div>
                  <a href="#" onClick={inert} aria-label={t("equipe.t09")} style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: "7px", marginTop: "auto", paddingTop: "6px", fontSize: "12px", fontWeight: "600", letterSpacing: "-0.005em", color: "rgb(26, 86, 219)", textDecoration: "none" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.76-2.05C21.1 8.65 22 10.9 22 14.03V21h-4v-6.2c0-1.48-.53-2.49-1.85-2.49-1.01 0-1.61.68-1.87 1.34-.1.24-.13.57-.13.9V21h-4V9Z" />
                    </svg>
                    {t("equipe.t10")}
                  </a>
                </div>
              </div>
              <div style={{ flex: "0 0 236px", scrollSnapAlign: "start", background: "rgb(250, 250, 248)", border: "1.5px dashed rgb(212, 212, 206)", borderRadius: "18px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ aspectRatio: "4 / 5", background: "rgb(233, 233, 228)" }}>
                  <TV3Slot src={IMG["77a2d108-04e1-4afc-a9b5-0a88395439a6"]} id="tp-jur-2" shape="rect" hostStyle={{ display: "block", width: "100%", height: "100%" }} />
                </div>
                <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: "7px", flex: "1 1 0%" }}>
                  <div style={{ fontSize: "17px", fontWeight: "800", letterSpacing: "-0.025em", color: "rgb(10, 10, 15)" }}>
                    {t("equipe.t11")}
                  </div>
                  <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "rgb(26, 86, 219)" }}>
                    {t("equipe.t12")}
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "400", lineHeight: "1.55", color: "rgb(90, 90, 101)" }}>
                    {t("equipe.t13")}
                  </div>
                  <a href="#" onClick={inert} aria-label={t("equipe.t14")} style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: "7px", marginTop: "auto", paddingTop: "6px", fontSize: "12px", fontWeight: "600", letterSpacing: "-0.005em", color: "rgb(26, 86, 219)", textDecoration: "none" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.76-2.05C21.1 8.65 22 10.9 22 14.03V21h-4v-6.2c0-1.48-.53-2.49-1.85-2.49-1.01 0-1.61.68-1.87 1.34-.1.24-.13.57-.13.9V21h-4V9Z" />
                    </svg>
                    {t("equipe.t15")}
                  </a>
                </div>
              </div>
              <div style={{ flex: "0 0 236px", scrollSnapAlign: "start", background: "rgb(250, 250, 248)", border: "1.5px dashed rgb(212, 212, 206)", borderRadius: "18px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ aspectRatio: "4 / 5", background: "rgb(233, 233, 228)" }}>
                  <TV3Slot src={IMG["8a8e253b-15c4-4c61-9303-ff18046cf220"]} id="tp-jur-3" shape="rect" hostStyle={{ display: "block", width: "100%", height: "100%" }} />
                </div>
                <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: "7px", flex: "1 1 0%" }}>
                  <div style={{ fontSize: "17px", fontWeight: "800", letterSpacing: "-0.025em", color: "rgb(10, 10, 15)" }}>
                    {t("equipe.t16")}
                  </div>
                  <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "rgb(26, 86, 219)" }}>
                    {t("equipe.t17")}
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "400", lineHeight: "1.55", color: "rgb(90, 90, 101)" }}>
                    {t("equipe.t18")}
                  </div>
                  <a href="#" onClick={inert} aria-label={t("equipe.t19")} style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: "7px", marginTop: "auto", paddingTop: "6px", fontSize: "12px", fontWeight: "600", letterSpacing: "-0.005em", color: "rgb(26, 86, 219)", textDecoration: "none" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.76-2.05C21.1 8.65 22 10.9 22 14.03V21h-4v-6.2c0-1.48-.53-2.49-1.85-2.49-1.01 0-1.61.68-1.87 1.34-.1.24-.13.57-.13.9V21h-4V9Z" />
                    </svg>
                    {t("equipe.t20")}
                  </a>
                </div>
              </div>
              <div style={{ flex: "0 0 236px", scrollSnapAlign: "start", background: "rgb(250, 250, 248)", border: "1.5px dashed rgb(212, 212, 206)", borderRadius: "18px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ aspectRatio: "4 / 5", background: "rgb(233, 233, 228)" }}>
                  <TV3Slot src={IMG["d1cd59af-ceb4-4deb-90c3-190e0463f73a"]} id="tp-jur-4" shape="rect" hostStyle={{ display: "block", width: "100%", height: "100%" }} />
                </div>
                <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: "7px", flex: "1 1 0%" }}>
                  <div style={{ fontSize: "17px", fontWeight: "800", letterSpacing: "-0.025em", color: "rgb(10, 10, 15)" }}>
                    {t("equipe.t21")}
                  </div>
                  <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "rgb(26, 86, 219)" }}>
                    {t("equipe.t22")}
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "400", lineHeight: "1.55", color: "rgb(90, 90, 101)" }}>
                    {t("equipe.t23")}
                  </div>
                  <a href="#" onClick={inert} aria-label={t("equipe.t24")} style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: "7px", marginTop: "auto", paddingTop: "6px", fontSize: "12px", fontWeight: "600", letterSpacing: "-0.005em", color: "rgb(26, 86, 219)", textDecoration: "none" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.76-2.05C21.1 8.65 22 10.9 22 14.03V21h-4v-6.2c0-1.48-.53-2.49-1.85-2.49-1.01 0-1.61.68-1.87 1.34-.1.24-.13.57-.13.9V21h-4V9Z" />
                    </svg>
                    {t("equipe.t25")}
                  </a>
                </div>
              </div>
              <div style={{ flex: "0 0 236px", scrollSnapAlign: "start", background: "rgb(250, 250, 248)", border: "1.5px dashed rgb(212, 212, 206)", borderRadius: "18px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ aspectRatio: "4 / 5", background: "rgb(233, 233, 228)" }}>
                  <TV3Slot src={IMG["3e2a996f-7709-4f9f-b230-b34069a2907b"]} id="tp-jur-5" shape="rect" hostStyle={{ display: "block", width: "100%", height: "100%" }} />
                </div>
                <div style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: "7px", flex: "1 1 0%" }}>
                  <div style={{ fontSize: "17px", fontWeight: "800", letterSpacing: "-0.025em", color: "rgb(10, 10, 15)" }}>
                    {t("equipe.t26")}
                  </div>
                  <div style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.07em", textTransform: "uppercase", color: "rgb(26, 86, 219)" }}>
                    {t("equipe.t27")}
                  </div>
                  <div style={{ fontSize: "13px", fontWeight: "400", lineHeight: "1.55", color: "rgb(90, 90, 101)" }}>
                    {t("equipe.t28")}
                  </div>
                  <a href="#" onClick={inert} aria-label={t("equipe.t29")} style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: "7px", marginTop: "auto", paddingTop: "6px", fontSize: "12px", fontWeight: "600", letterSpacing: "-0.005em", color: "rgb(26, 86, 219)", textDecoration: "none" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.76-2.05C21.1 8.65 22 10.9 22 14.03V21h-4v-6.2c0-1.48-.53-2.49-1.85-2.49-1.01 0-1.61.68-1.87 1.34-.1.24-.13.57-.13.9V21h-4V9Z" />
                    </svg>
                    {t("equipe.t30")}
                  </a>
                </div>
              </div>
            </div>
            <p style={{ margin: "0", fontSize: "13.5px", fontWeight: "500", color: "rgb(90, 90, 101)" }}>
              {t("equipe.t31")}
            </p>
          </div>
        </section>
        <section id="parole" style={{ padding: "88px 40px", background: "rgb(255, 255, 255)", borderTop: "1px solid rgb(232, 232, 228)" }}>
          <div style={{ maxWidth: "1020px", margin: "0px auto" }}>
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <h2 style={{ fontSize: "clamp(28px, 3.4vw, 40px)", fontFamily: "'Inter Tight', -apple-system, BlinkMacSystemFont, system-ui, sans-serif", fontWeight: "600", letterSpacing: "-0.032em", lineHeight: "1.14", margin: "0px 0px 12px", color: "rgb(10, 10, 15)" }}>
                {t("parole.t01")}
              </h2>
              <p style={{ margin: "0px auto", maxWidth: "540px", fontSize: "15.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)" }}>
                {t("parole.t02")}
              </p>
            </div>
            <div className="tp-voice-grid">
              <div style={{ background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", borderRadius: "18px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ position: "relative", aspectRatio: "16 / 9", background: "#E9E9E4" }}>
                  <TV3Slot src={IMG["2dbc1d78-228d-4595-bcef-bdf0b556c5ce"]} id="tp-ep1" shape="rect" hostStyle={{ display: "block", width: "100%", height: "100%" }} />
                  {" "}
                  <span className="tp-play" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "62px", height: "62px", borderRadius: "50%", background: "rgba(255,255,255,0.94)", boxShadow: "0 10px 30px rgba(10,10,15,0.22)", display: "flex", alignItems: "center", justifyContent: "center", transition: "transform .18s ease", pointerEvents: "none" }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                      <polygon points="8 5 19 12 8 19" fill="#1a56db" stroke="none" />
                    </svg>
                  </span>
                  {" "}
                  <span style={{ position: "absolute", right: "12px", bottom: "12px", background: "rgba(10,10,15,0.78)", color: "rgb(255, 255, 255)", fontSize: "11.5px", fontWeight: "700", letterSpacing: "0.02em", padding: "4px 10px", borderRadius: "999px" }}>
                    {t("parole.t03")}
                  </span>
                </div>
                <div style={{ padding: "20px 22px 22px" }}>
                  <div style={{ fontSize: "17.5px", fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.3", color: "rgb(10, 10, 15)" }}>
                    {t("parole.t04")}
                  </div>
                </div>
              </div>
              <div style={{ background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", borderRadius: "18px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ position: "relative", aspectRatio: "16 / 9", background: "#E9E9E4" }}>
                  <TV3Slot src={IMG["13e0d6c0-dfe3-45e9-9671-7d9bd7d5d0f9"]} id="tp-ep2" shape="rect" hostStyle={{ display: "block", width: "100%", height: "100%" }} />
                  {" "}
                  <span className="tp-play" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "62px", height: "62px", borderRadius: "50%", background: "rgba(255,255,255,0.94)", boxShadow: "0 10px 30px rgba(10,10,15,0.22)", display: "flex", alignItems: "center", justifyContent: "center", transition: "transform .18s ease", pointerEvents: "none" }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
                      <polygon points="8 5 19 12 8 19" fill="#1a56db" stroke="none" />
                    </svg>
                  </span>
                  {" "}
                  <span style={{ position: "absolute", right: "12px", bottom: "12px", background: "rgba(10,10,15,0.78)", color: "rgb(255, 255, 255)", fontSize: "11.5px", fontWeight: "700", letterSpacing: "0.02em", padding: "4px 10px", borderRadius: "999px" }}>
                    {t("parole.t05")}
                  </span>
                </div>
                <div style={{ padding: "20px 22px 22px" }}>
                  <div style={{ fontSize: "17.5px", fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.3", color: "rgb(10, 10, 15)" }}>
                    {t("parole.t06")}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ margin: "22px 0 0", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
              <p style={{ margin: "0", textAlign: "center", fontSize: "13.5px", fontWeight: "500", color: "rgb(90, 90, 101)" }}>
                {t("parole.t07")}
              </p>
              <a href="#" onClick={inert} className="tp-cta-ghost" style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 234, 228)", borderRadius: "12px", padding: "11px 20px", fontSize: "14px", fontWeight: "700", letterSpacing: "-0.01em", color: "rgb(10, 10, 15)", textDecoration: "none" }}>
                {t("parole.t08")}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>
            </div>
          </div>
        </section>
        <section id="chemin" style={{ padding: "84px 40px", background: "rgb(244, 244, 241)", borderTop: "1px solid rgb(232, 232, 228)", borderBottom: "1px solid rgb(232, 232, 228)" }}>
          <div style={{ maxWidth: "1020px", margin: "0px auto" }}>
            <div style={{ maxWidth: "640px", marginBottom: "36px" }}>
              <h2 style={{ fontSize: "clamp(28px, 3.4vw, 40px)", fontFamily: "'Inter Tight', -apple-system, BlinkMacSystemFont, system-ui, sans-serif", fontWeight: "600", letterSpacing: "-0.032em", lineHeight: "1.14", margin: "0px 0px 12px", color: "rgb(10, 10, 15)" }}>
                {t("chemin.t01")}
              </h2>
              <p style={{ margin: "0px", fontSize: "15.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)" }}>
                {t("chemin.t02")}
              </p>
            </div>
            <div className="tp-chemin-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px" }}>
              <div style={{ background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", borderRadius: "16px", padding: "22px 20px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ flex: "0 0 auto", width: "38px", height: "38px", borderRadius: "11px", background: "#EFF6FF", color: "#1A56DB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </span>
                  <span aria-hidden="true" className="tp-chemin-line" style={{ flex: "1 1 0%", height: "2px", background: "rgb(232, 232, 228)" }}></span>
                  <span style={{ flex: "0 0 auto", fontSize: "11.5px", fontWeight: "800", letterSpacing: "0.04em", color: "rgb(168, 168, 160)" }}>
                    {t("chemin.t03")}
                  </span>
                </div>
                <div style={{ fontSize: "16.5px", fontWeight: "700", letterSpacing: "-0.022em", lineHeight: "1.28", color: "rgb(10, 10, 15)" }}>
                  {t("chemin.t04")}
                </div>
                <div style={{ fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)" }}>
                  {t("chemin.t05")}
                </div>
                <div style={{ marginTop: "auto", paddingTop: "4px" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgb(240, 253, 244)", border: "1px solid rgb(187, 247, 208)", borderRadius: "999px", padding: "5px 12px", fontSize: "12px", fontWeight: "700", letterSpacing: "-0.005em", color: "rgb(21, 128, 61)" }}>
                    {t("chemin.t06")}
                  </span>
                </div>
              </div>
              <div style={{ background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", borderRadius: "16px", padding: "22px 20px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ flex: "0 0 auto", width: "38px", height: "38px", borderRadius: "11px", background: "#ECFEFF", color: "#0891B2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>
                  <span aria-hidden="true" className="tp-chemin-line" style={{ flex: "1 1 0%", height: "2px", background: "rgb(232, 232, 228)" }}></span>
                  <span style={{ flex: "0 0 auto", fontSize: "11.5px", fontWeight: "800", letterSpacing: "0.04em", color: "rgb(168, 168, 160)" }}>
                    {t("chemin.t07")}
                  </span>
                </div>
                <div style={{ fontSize: "16.5px", fontWeight: "700", letterSpacing: "-0.022em", lineHeight: "1.28", color: "rgb(10, 10, 15)" }}>
                  {t("chemin.t08")}
                </div>
                <div style={{ fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)" }}>
                  {t("chemin.t09")}
                </div>
                <div style={{ marginTop: "auto", paddingTop: "4px" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgb(240, 253, 244)", border: "1px solid rgb(187, 247, 208)", borderRadius: "999px", padding: "5px 12px", fontSize: "12px", fontWeight: "700", letterSpacing: "-0.005em", color: "rgb(21, 128, 61)" }}>
                    {t("chemin.t10")}
                  </span>
                </div>
              </div>
              <div style={{ background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", borderRadius: "16px", padding: "22px 20px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ flex: "0 0 auto", width: "38px", height: "38px", borderRadius: "11px", background: "#EFF6FF", color: "#1A56DB", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                  <span aria-hidden="true" className="tp-chemin-line" style={{ flex: "1 1 0%", height: "2px", background: "rgb(232, 232, 228)" }}></span>
                  <span style={{ flex: "0 0 auto", fontSize: "11.5px", fontWeight: "800", letterSpacing: "0.04em", color: "rgb(168, 168, 160)" }}>
                    {t("chemin.t11")}
                  </span>
                </div>
                <div style={{ fontSize: "16.5px", fontWeight: "700", letterSpacing: "-0.022em", lineHeight: "1.28", color: "rgb(10, 10, 15)" }}>
                  {t("chemin.t12")}
                </div>
                <div style={{ fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)" }}>
                  {t("chemin.t13")}
                </div>
                <div style={{ marginTop: "auto", paddingTop: "4px" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "rgb(239, 246, 255)", border: "1px solid rgb(219, 234, 254)", borderRadius: "999px", padding: "5px 12px", fontSize: "12px", fontWeight: "700", letterSpacing: "-0.005em", color: "rgb(26, 86, 219)" }}>
                    {t("chemin.t14")}
                  </span>
                </div>
              </div>
            </div>
            <div style={{ marginTop: "44px", display: "flex", flexDirection: "column", gap: "6px", marginBottom: "18px" }}>
              <div style={{ fontSize: "11px", fontWeight: "800", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgb(180, 83, 9)" }}>
                {t("chemin.t15")}
              </div>
              <p style={{ margin: "0", maxWidth: "620px", fontSize: "15px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)" }}>
                {t("chemin.t16")}
              </p>
            </div>
            <div className="tp-av-track" style={{ position: "relative", background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", borderRadius: "18px", padding: "26px 28px 28px", marginBottom: "0", boxShadow: "0 6px 24px rgba(10,10,15,0.04)", overflow: "hidden" }}>
              <div className="tp-av-rail-row" aria-hidden="true" style={{ position: "relative", height: "34px", marginBottom: "14px" }}>
                <div style={{ position: "absolute", left: "16.667%", right: "16.667%", top: "20px", height: "3px", borderRadius: "999px", background: "rgb(238, 238, 233)" }}></div>
                <div className="tp-rail-fill" style={{ position: "absolute", left: "16.667%", right: "16.667%", top: "20px", height: "3px", borderRadius: "999px", background: "linear-gradient(90deg, #1A56DB 0%, #1A56DB 40%, #B45309 72%, #15803D 100%)", transformOrigin: "left center" }}></div>
                <span className="tp-av-dot" data-i="0" style={{ position: "absolute", left: "16.667%", top: "15px", transform: "translateX(-50%)", width: "13px", height: "13px", borderRadius: "50%", background: "rgb(255, 255, 255)", border: "3px solid rgb(226, 226, 220)" }}></span>
                <span className="tp-av-dot" data-i="1" style={{ position: "absolute", left: "50%", top: "15px", transform: "translateX(-50%)", width: "13px", height: "13px", borderRadius: "50%", background: "rgb(255, 255, 255)", border: "3px solid rgb(226, 226, 220)" }}></span>
                <span className="tp-av-dot" data-i="2" style={{ position: "absolute", left: "83.333%", top: "15px", transform: "translateX(-50%)", width: "13px", height: "13px", borderRadius: "50%", background: "rgb(255, 255, 255)", border: "3px solid rgb(226, 226, 220)" }}></span>
                <div className="tp-pkg" style={{ position: "absolute", top: "3px", left: "16.667%", display: "flex", alignItems: "center", gap: "7px", background: "rgb(255, 255, 255)", border: "1px solid rgb(226, 226, 220)", borderRadius: "10px", padding: "5px 11px 5px 9px", boxShadow: "0 10px 24px rgba(10,10,15,0.18)", whiteSpace: "nowrap" }}>
                  <span style={{ display: "flex" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1A56DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
                      <polyline points="14 3 14 8 19 8" />
                    </svg>
                  </span>
                  <span style={{ fontSize: "10.5px", fontWeight: "800", letterSpacing: "-0.005em", color: "rgb(42, 42, 53)" }}>
                    {t("chemin.t17")}
                  </span>
                </div>
              </div>
              <div className="tp-av-steps" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "26px" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                  <span className="tp-av-chip" data-i="0" style={{ width: "40px", height: "40px", borderRadius: "11px", background: "rgb(239, 246, 255)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1A56DB" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
                      <polyline points="14 3 14 8 19 8" />
                    </svg>
                  </span>
                  <span className="tp-av-num" data-i="0" style={{ marginTop: "12px", fontSize: "10.5px", fontWeight: "800", letterSpacing: "0.12em", color: "#6b7280" }}>
                    {t("chemin.t18")}
                  </span>
                  <div style={{ marginTop: "7px", fontSize: "16px", fontWeight: "700", letterSpacing: "-0.022em", lineHeight: "1.3", color: "rgb(10, 10, 15)", textWrap: "pretty" }}>
                    {t("chemin.t19")}
                  </div>
                  <div style={{ marginTop: "8px", maxWidth: "25ch", fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)", textWrap: "pretty" }}>
                    {t("chemin.t20")}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                  <span className="tp-av-chip" data-i="1" style={{ width: "40px", height: "40px", borderRadius: "11px", background: "rgb(254, 243, 199)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 3v18" />
                      <path d="M4 7h16" />
                      <path d="M4 7 2 13h4Z" />
                      <path d="M20 7l-2 6h4Z" />
                      <path d="M9 21h6" />
                    </svg>
                  </span>
                  <span className="tp-av-num" data-i="1" style={{ marginTop: "12px", fontSize: "10.5px", fontWeight: "800", letterSpacing: "0.12em", color: "#6b7280" }}>
                    {t("chemin.t21")}
                  </span>
                  <div style={{ marginTop: "7px", fontSize: "16px", fontWeight: "700", letterSpacing: "-0.022em", lineHeight: "1.3", color: "rgb(10, 10, 15)", textWrap: "pretty" }}>
                    {t("chemin.t22")}
                  </div>
                  <div style={{ marginTop: "8px", maxWidth: "25ch", fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)", textWrap: "pretty" }}>
                    {t("chemin.t23")}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                  <span className="tp-av-chip" data-i="2" style={{ width: "40px", height: "40px", borderRadius: "11px", background: "rgb(240, 253, 244)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#15803D" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                  </span>
                  <span className="tp-av-num" data-i="2" style={{ marginTop: "12px", fontSize: "10.5px", fontWeight: "800", letterSpacing: "0.12em", color: "#6b7280" }}>
                    {t("chemin.t24")}
                  </span>
                  <div style={{ marginTop: "7px", fontSize: "16px", fontWeight: "700", letterSpacing: "-0.022em", lineHeight: "1.3", color: "rgb(10, 10, 15)", textWrap: "pretty" }}>
                    {t("chemin.t25")}
                  </div>
                  <div style={{ marginTop: "8px", maxWidth: "25ch", fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)", textWrap: "pretty" }}>
                    {t("chemin.t26")}
                  </div>
                </div>
              </div>
            </div>
            <p style={{ margin: "26px 0 0", fontSize: "13.5px", fontWeight: "500", color: "rgb(90, 90, 101)" }}>
              {t("chemin.t27")}
            </p>
          </div>
        </section>
        <section id="faq" style={{ padding: "60px 40px", background: "rgb(255, 255, 255)", borderTop: "1px solid rgb(236, 236, 234)" }}>
          <div style={{ maxWidth: "720px", margin: "0px auto" }}>
            <div style={{ textAlign: "center", marginBottom: "26px" }}>
              <h2 style={{ fontSize: "clamp(24px, 2.6vw, 30px)", fontFamily: "'Inter Tight', -apple-system, BlinkMacSystemFont, system-ui, sans-serif", fontWeight: "600", letterSpacing: "-0.032em", lineHeight: "1.14", margin: "0px 0px 12px", color: "rgb(10, 10, 15)" }}>
                {t("faq.t01")}
              </h2>
            </div>
            <div style={{ background: "rgb(255, 255, 255)", border: "1px solid rgb(236, 236, 234)", borderRadius: "18px", overflow: "hidden" }}>
              <div style={{  }}>
                <button type="button" className="tp-faq-btn" data-open={faq === 0 ? 'true' : 'false'} onClick={() => toggleFaq(0)} aria-expanded={faq === 0 ? 'true' : 'false'} style={{ width: "100%", display: "flex", alignItems: "center", gap: "16px", textAlign: "left", background: "rgb(255, 255, 255)", borderWidth: "medium", borderStyle: "none", padding: "15px 20px", fontFamily: "inherit", cursor: "pointer" }}>
                  <span style={{ flex: "1 1 0%", minWidth: "0", fontSize: "14.5px", fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.4", color: "rgb(10, 10, 15)" }}>
                    {t("faq.t02")}
                  </span>
                  <span className="tp-faq-chev" aria-hidden="true" style={{ flex: "0 0 auto", color: "rgb(138, 138, 149)" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                {" "}
                {faq === 0 && (
                  <>
                  <div style={{ padding: "0px 20px 16px", fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)", maxWidth: "640px" }}>
                    {t("faq.t03")}
                  </div>
                  </>
                )}
              </div>
              <div style={{ borderTop: "1px solid rgb(244, 244, 241)" }}>
                <button type="button" className="tp-faq-btn" data-open={faq === 1 ? 'true' : 'false'} onClick={() => toggleFaq(1)} aria-expanded={faq === 1 ? 'true' : 'false'} style={{ width: "100%", display: "flex", alignItems: "center", gap: "16px", textAlign: "left", background: "rgb(255, 255, 255)", borderWidth: "medium", borderStyle: "none", padding: "15px 20px", fontFamily: "inherit", cursor: "pointer" }}>
                  <span style={{ flex: "1 1 0%", minWidth: "0", fontSize: "14.5px", fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.4", color: "rgb(10, 10, 15)" }}>
                    {t("faq.t04")}
                  </span>
                  <span className="tp-faq-chev" aria-hidden="true" style={{ flex: "0 0 auto", color: "rgb(138, 138, 149)" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                {" "}
                {faq === 1 && (
                  <>
                  <div style={{ padding: "0px 20px 16px", fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)", maxWidth: "640px" }}>
                    {t("faq.t05")}
                  </div>
                  </>
                )}
              </div>
              <div style={{ borderTop: "1px solid rgb(244, 244, 241)" }}>
                <button type="button" className="tp-faq-btn" data-open={faq === 2 ? 'true' : 'false'} onClick={() => toggleFaq(2)} aria-expanded={faq === 2 ? 'true' : 'false'} style={{ width: "100%", display: "flex", alignItems: "center", gap: "16px", textAlign: "left", background: "rgb(255, 255, 255)", borderWidth: "medium", borderStyle: "none", padding: "15px 20px", fontFamily: "inherit", cursor: "pointer" }}>
                  <span style={{ flex: "1 1 0%", minWidth: "0", fontSize: "14.5px", fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.4", color: "rgb(10, 10, 15)" }}>
                    {t("faq.t06")}
                  </span>
                  <span className="tp-faq-chev" aria-hidden="true" style={{ flex: "0 0 auto", color: "rgb(138, 138, 149)" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                {" "}
                {faq === 2 && (
                  <>
                  <div style={{ padding: "0px 20px 16px", fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)", maxWidth: "640px" }}>
                    {t("faq.t07")}
                  </div>
                  </>
                )}
              </div>
              <div style={{ borderTop: "1px solid rgb(244, 244, 241)" }}>
                <button type="button" className="tp-faq-btn" data-open={faq === 3 ? 'true' : 'false'} onClick={() => toggleFaq(3)} aria-expanded={faq === 3 ? 'true' : 'false'} style={{ width: "100%", display: "flex", alignItems: "center", gap: "16px", textAlign: "left", background: "rgb(255, 255, 255)", borderWidth: "medium", borderStyle: "none", padding: "15px 20px", fontFamily: "inherit", cursor: "pointer" }}>
                  <span style={{ flex: "1 1 0%", minWidth: "0", fontSize: "14.5px", fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.4", color: "rgb(10, 10, 15)" }}>
                    {t("faq.t08")}
                  </span>
                  <span className="tp-faq-chev" aria-hidden="true" style={{ flex: "0 0 auto", color: "rgb(138, 138, 149)" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                {" "}
                {faq === 3 && (
                  <>
                  <div style={{ padding: "0px 20px 16px", fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)", maxWidth: "640px" }}>
                    {t("faq.t09")}
                  </div>
                  </>
                )}
              </div>
              <div style={{ borderTop: "1px solid rgb(244, 244, 241)" }}>
                <button type="button" className="tp-faq-btn" data-open={faq === 4 ? 'true' : 'false'} onClick={() => toggleFaq(4)} aria-expanded={faq === 4 ? 'true' : 'false'} style={{ width: "100%", display: "flex", alignItems: "center", gap: "16px", textAlign: "left", background: "rgb(255, 255, 255)", borderWidth: "medium", borderStyle: "none", padding: "15px 20px", fontFamily: "inherit", cursor: "pointer" }}>
                  <span style={{ flex: "1 1 0%", minWidth: "0", fontSize: "14.5px", fontWeight: "700", letterSpacing: "-0.02em", lineHeight: "1.4", color: "rgb(10, 10, 15)" }}>
                    {t("faq.t10")}
                  </span>
                  <span className="tp-faq-chev" aria-hidden="true" style={{ flex: "0 0 auto", color: "rgb(138, 138, 149)" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </button>
                {" "}
                {faq === 4 && (
                  <>
                  <div style={{ padding: "0px 20px 16px", fontSize: "13.5px", fontWeight: "400", lineHeight: "1.6", color: "rgb(90, 90, 101)", maxWidth: "640px" }}>
                    {t("faq.t11")}
                  </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
        <section id="cta" style={{ padding: "0 40px 96px", background: "rgb(255, 255, 255)" }}>
          <div style={{ maxWidth: "760px", margin: "0px auto", textAlign: "center" }}>
            <h2 style={{ fontSize: "clamp(24px, 2.8vw, 32px)", fontFamily: "'Inter Tight', -apple-system, BlinkMacSystemFont, system-ui, sans-serif", fontWeight: "600", letterSpacing: "-0.032em", lineHeight: "1.18", margin: "0px 0px 14px", color: "rgb(10, 10, 15)" }}>
              {t("cta.t01")}
            </h2>
            <p style={{ margin: "0px auto 26px", maxWidth: "540px", fontSize: "15px", fontWeight: "400", lineHeight: "1.65", color: "rgb(90, 90, 101)" }}>
              {t("cta.t02")}
            </p>
            <a className="tp-cta" href="#" onClick={goStart} style={{ display: "inline-flex", alignItems: "center", gap: "9px", background: "rgb(26, 86, 219)", color: "rgb(255, 255, 255)", fontSize: "15.5px", fontWeight: "600", letterSpacing: "-0.01em", padding: "15px 30px", borderRadius: "12px", boxShadow: "rgba(26, 86, 219, 0.2) 0px 4px 16px", textDecoration: "none" }}>
              {t("cta.t03")}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>
        </section>
      </main>
      <SiteFooterV2 />
    </div>
  );
}
